/**
 * Admin-only dashboard persona previews. Builds mock dogs/subscriptions for
 * each customer type so admins can see how the dashboard renders without
 * needing a real account in that state. Mock meals reference REAL pouch
 * variants from the products list so titles, images, and pack sizes resolve
 * exactly as they would for a genuine customer.
 */

import type { Dog } from "../types";

export type PersonaKey =
  | "real"
  | "bundle"
  | "multi-dog"
  | "legacy"
  | "legacy-multi-dog"
  | "mixed"
  | "paused"
  | "new";

export const PERSONAS: { key: PersonaKey; label: string }[] = [
  { key: "real", label: "My real data" },
  { key: "bundle", label: "Active · 1 dog" },
  { key: "multi-dog", label: "Active · 2 dogs" },
  { key: "legacy", label: "Legacy recipes" },
  { key: "legacy-multi-dog", label: "Legacy · 2 dogs" },
  { key: "mixed", label: "Bundle + legacy" },
  { key: "paused", label: "Paused plan" },
  { key: "new", label: "New customer" },
];

export type PersonaData = {
  dogs: Dog[];
  subscriptions: any[];
  addresses: any[];
  nextDeliveryDate: string | null;
};

const MOCK_ADDRESS = {
  id: "preview-address",
  first_name: "Richard",
  last_name: "Preview",
  address1: "12 Paws Lane",
  city: "Brighton",
  province: "East Sussex",
  zip: "BN1 1AA",
  country: "United Kingdom",
};

const RECIPE_KEYWORDS = ["sammy", "vinnie", "loki", "boot", "tilly"];

function mockDog(
  name: string,
  breed: string,
  ageYears: number,
  dailyCalories: number,
  dailyGrams: number
): Dog {
  return {
    id: `preview-${name.toLowerCase()}`,
    name,
    breed,
    ageYears,
    ageMonths: 0,
    dailyCalories,
    dailyGrams,
    weightKg: 14,
  } as unknown as Dog;
}

type RecipeVariant = { variantId: string; grams: number };

/** Real pouch recipe variants (prefers 600g packs) from the product catalog. */
function findRecipeVariants(products: any[]): RecipeVariant[] {
  const out: RecipeVariant[] = [];
  for (const pWrap of products || []) {
    const p = pWrap?.product ?? pWrap;
    const title = String(p?.title || "").toLowerCase();
    if (!RECIPE_KEYWORDS.some((k) => title.includes(k))) continue;
    const variants = p?.variants || [];
    const preferred =
      variants.find((v: any) => Number(v?.grams) === 600 || String(v?.title || "").trim() === "600g") ||
      variants[0];
    if (!preferred?.id) continue;
    out.push({ variantId: String(preferred.id), grams: Number(preferred.grams) || 600 });
  }
  return out;
}

function futureDate(daysAhead: number): string {
  const d = new Date();
  d.setDate(d.getDate() + daysAhead);
  return d.toISOString().slice(0, 10);
}

function mockBundleSub(
  id: string,
  recipes: RecipeVariant[],
  dogName: string,
  status: "active" | "cancelled" = "active"
) {
  const meals = recipes.map((r) => ({ variantId: r.variantId, count: 2 }));
  const packSize = recipes[0]?.grams || 600;
  return {
    id,
    status,
    product_title: "Meal Plan",
    variant_title: `${packSize}g`,
    address_id: "preview-address",
    next_charge_scheduled_at: futureDate(4),
    properties: [
      { name: "_paws_meals_json", value: JSON.stringify(meals) },
      { name: "_paws_pack_size_grams", value: String(packSize) },
      { name: "_paws_plan_days", value: "14" },
      { name: "_paws_dog_json", value: JSON.stringify({ name: dogName, allergies: [] }) },
    ],
  };
}

function mockLegacySub(id: string, recipe: RecipeVariant, quantity: number) {
  return {
    id,
    status: "active",
    product_title: "Recipe",
    variant_title: `${recipe.grams}g`,
    shopify_variant_id: recipe.variantId,
    quantity,
    price: "9.50",
    address_id: "preview-address",
    next_charge_scheduled_at: futureDate(6),
    properties: [],
  };
}

export function buildPersonaData(persona: PersonaKey, products: any[]): PersonaData | null {
  if (persona === "real") return null;

  const recipes = findRecipeVariants(products);
  const coco = mockDog("Coco", "Sprocker Spaniel", 5, 532, 400);
  const rex = mockDog("Rex", "Labrador", 3, 890, 650);

  switch (persona) {
    case "bundle":
      return {
        addresses: [MOCK_ADDRESS],
        dogs: [coco],
        subscriptions: [mockBundleSub("preview-sub-1", recipes.slice(0, 3), "Coco")],
        nextDeliveryDate: futureDate(5),
      };
    case "multi-dog":
      return {
        addresses: [MOCK_ADDRESS],
        dogs: [coco, rex],
        subscriptions: [
          mockBundleSub("preview-sub-1", recipes.slice(0, 3), "Coco"),
          mockBundleSub("preview-sub-2", recipes.slice(2, 5), "Rex"),
        ],
        nextDeliveryDate: futureDate(5),
      };
    case "legacy":
      return {
        addresses: [MOCK_ADDRESS],
        dogs: [coco],
        subscriptions: recipes.slice(0, 2).map((r, i) => mockLegacySub(`preview-legacy-${i}`, r, 2)),
        nextDeliveryDate: futureDate(6),
      };
    case "legacy-multi-dog":
      // Legacy subs carry no dog metadata, so these dogs are intentionally
      // NOT linked to the recipes — mirrors real pre-migration households.
      return {
        dogs: [coco, rex],
        subscriptions: recipes.slice(0, 3).map((r, i) => mockLegacySub(`preview-legacy-${i}`, r, 2)),
        addresses: [MOCK_ADDRESS],
        nextDeliveryDate: futureDate(6),
      };
    case "mixed":
      return {
        addresses: [MOCK_ADDRESS],
        dogs: [coco],
        subscriptions: [
          mockBundleSub("preview-sub-1", recipes.slice(0, 3), "Coco"),
          ...recipes.slice(3, 5).map((r, i) => mockLegacySub(`preview-legacy-${i}`, r, 1)),
        ],
        nextDeliveryDate: futureDate(5),
      };
    case "paused":
      return {
        addresses: [MOCK_ADDRESS],
        dogs: [coco],
        subscriptions: [mockBundleSub("preview-sub-1", recipes.slice(0, 3), "Coco", "cancelled")],
        nextDeliveryDate: null,
      };
    case "new":
      return { dogs: [], subscriptions: [], addresses: [], nextDeliveryDate: null };
    default:
      return null;
  }
}
